/* Dialoge und Drawer auf Basis von <dialog>. Öffnen per data-dialog-open="id", schließen per data-dialog-close. */

/*
  Von unten eingefahrene Flaechen lassen sich nach unten wegziehen. Ziehflaeche sind Griff und
  Kopfzeile, nicht der Inhalt, damit das Scrollen darin frei bleibt.

  Bewusst eine freie Funktion und keine Methode: Die mobile Navigation ist kein `<dialog>` —
  sie teilt ihr Markup mit der Leiste am Rechner —, soll sich aber genauso anfuehlen. So steht
  die Mechanik einmal da statt zweimal.

  `aktiv` entscheidet bei jedem Griff neu, ob gerade gezogen werden darf; `schliessen` wird
  aufgerufen, wenn weit genug gezogen wurde.
*/
function wegziehbar(flaeche, aktiv, schliessen) {
  const zonen = [...flaeche.querySelectorAll('.drawer__handle, .drawer__header, [data-drawer-grab]')];
  if (!zonen.length) return;

  let start = 0;
  let weg = 0;
  let zieht = false;
  let moeglich = false;

  const loslassen = () => {
    moeglich = false;
    if (!zieht) return;
    zieht = false;
    flaeche.style.transition = '';
    /* Ab einem Viertel der Hoehe, hoechstens aber 120 px, gilt es als Wegwischen */
    if (weg > Math.min(120, flaeche.offsetHeight * 0.25)) {
      flaeche.style.translate = '0 100%';
      schliessen();
      window.setTimeout(() => {
        flaeche.style.translate = '';
      }, 300);
    } else {
      flaeche.style.translate = '';
    }
    weg = 0;
  };

  zonen.forEach((zone) => {
    zone.addEventListener('pointerdown', (event) => {
      /* Knoepfe und Links behalten ihre Aufgabe, dort wird nie gezogen */
      if (!aktiv() || event.target.closest('button, a')) return;
      moeglich = true;
      start = event.clientY;
      weg = 0;
    });
    zone.addEventListener('pointermove', (event) => {
      if (!moeglich && !zieht) return;
      const strecke = event.clientY - start;
      /*
        Erst ab einem echten Zug nach unten uebernehmen. Sonst waere in der Suche kein
        Antippen des Feldes mehr moeglich, denn dort ist die Kopfzeile fast nur das Feld.
      */
      if (!zieht) {
        if (strecke < 10) return;
        zieht = true;
        flaeche.style.transition = 'none';
        zone.setPointerCapture(event.pointerId);
      }
      weg = Math.max(0, strecke);
      flaeche.style.translate = `0 ${weg}px`;
    });
    zone.addEventListener('pointerup', loslassen);
    zone.addEventListener('pointercancel', loslassen);
  });
}

/*
  Flaechen ausserhalb des Dialog-Systems melden sich ueber `data-sheet-drag` an und erfahren
  ueber `sheet:dismiss`, dass weit genug gezogen wurde. Ueber ein Ereignis statt ueber einen
  Import: Relative Modul-Importe tragen im Theme keine Version in der Adresse.
*/
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-sheet-drag]').forEach((flaeche) => {
    wegziehbar(
      flaeche,
      () => flaeche.hasAttribute('data-sheet-drag-aktiv'),
      () => flaeche.dispatchEvent(new CustomEvent('sheet:dismiss', { bubbles: true })),
    );
  });
});

class KgDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.querySelectorAll('[data-dialog-close]').forEach((element) => {
      element.addEventListener('click', () => this.close());
    });
    this.initSheet();
  }

  initSheet() {
    wegziehbar(this.dialog, () => this.istSheet(), () => this.close());
  }

  istSheet() {
    return istSheet(this.dialog);
  }

  get isOpen() {
    return Boolean(this.dialog?.open);
  }

  open(trigger) {
    if (!this.dialog || this.dialog.open) return;

    this.trigger = trigger;

    /*
      Abdunklung setzen, BEVOR der Schuber die oberste Ebene betritt.

      Am Geraet gemessen: Safari misst die Farbe seiner Leisten in dem Moment, in dem sich
      die oberste Ebene aendert. Beim Oeffnen stand die Abdunklung da noch auf Deckkraft
      null — die Einblendung beginnt erst danach —, also las Safari Weiss. Beim Schliessen
      war sie noch sichtbar, und die Leiste uebernahm ihre Farbe.

      Ein erzwungener Umbruch genuegt dafuer nicht: Der Wert steht dann zwar, aber es
      wurde noch kein Bild damit gezeichnet. Deshalb zwei Bildaufbauten Vorlauf.
    */
    if (istSheet(this.dialog) && !this.dunkelBereit) {
      document.documentElement.setAttribute('data-schuber-dunkel', '');
      this.dunkelBereit = true;
      /* Zwei Bildaufbauten: Der erste zeichnet die Abdunklung, der zweite stellt sicher,
         dass sie wirklich auf dem Schirm stand, bevor der Schuber die oberste Ebene betritt. */
      requestAnimationFrame(() => requestAnimationFrame(() => this.open(trigger)));
      return;
    }

    /* Erst hier zuruecksetzen, sonst liefe der zweite Aufruf wieder in die Vorbereitung. */
    this.dunkelBereit = false;

    /*
      Geht dieser Schuber ueber einem anderen auf, bekommt er eine eigene Abdunklung —
      aber nur fuer den Teil des darunterliegenden, der oben herausschaut. Oben dunkelt
      bereits die klebende Flaeche ab, dort waere eine zweite Lage zu dunkel.
    */
    /* Die Navigation zaehlt mit: Sie ist in der Schuber-Darstellung ein Schuber wie jeder andere. */
    this.dialog.dataset.schuberRang = String(naechsterRang());

    this.dialog.showModal();
    stapelPflegen();
    if (istSheet(this.dialog)) ankuendigungWeg();
    const ziel = this.dialog.querySelector('[data-autofocus]');
    if (ziel) {
      /*
        Erst fokussieren, wenn der Schuber steht. Fokussiert man waehrend der Einfahrt,
        sitzt das Feld noch unten am Rand, und Safari schiebt daraufhin den ganzen
        Layoutbereich nach oben, damit es ueber der Tastatur liegt; zurueck stellt es
        das nie. Der Preis dafuer: iOS oeffnet die Tastatur nur bei einem Fokus
        innerhalb der Fingerbewegung, hier also erst beim Antippen des Feldes.
        Bewusst so entschieden, ein verschobenes Layout waere schlimmer.
      */
      const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.setTimeout(() => ziel.focus({ preventScroll: true }), ruhig ? 0 : 320);
    } else if (!this.dialog.querySelector('[autofocus]')) {
      this.dialog.setAttribute('tabindex', '-1');
      this.dialog.focus();
    }
    this.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
  }

  close() {
    if (!this.dialog?.open || this.closing) return;
    this.closing = true;
    this.dialog.classList.add('is-closing');
    /*
      Sperre sofort loesen, nicht erst wenn der Dialog zugeht.

      Waehrend ein Schuber offen ist, liegt der Koerper auf `position: fixed`. Beim Loesen
      schaltet das zurueck und springt per `scrollTo` an die gemerkte Stelle — auf iOS sieht
      man das als kurzes Flackern. Haengt die Sperre am Attribut des Dialogs, passiert das
      erst nach der Ausfahrt, also auf der schon wieder freien Seite. Hier geloest, liegt der
      Schuber noch davor und verdeckt es. Das Menue macht es seit jeher so (header.js), und
      genau darum flackerte es dort nie.
    */
    sperrePruefen();
    const finish = () => {
      /*
        Reihenfolge ist hier entscheidend: Erst die Abdunklung wegnehmen, dann `is-closing`.

        Andersherum passte zwischen beiden Schritten wieder
        `html[data-schuber-dunkel] .drawer-scrim { opacity: 1; transition: none }` — die
        Flaeche sprang also auf volle Staerke und blendete danach ein zweites Mal aus. Am
        Geraet sah man das als kurzes Nachdunkeln, nachdem der Schuber schon weg war. Der
        Zeitgeber hier und der Uebergang im CSS dauern beide 250 ms; dass der Uebergang beim
        Feuern noch ein paar Millisekunden offen hat, reicht fuer den Sprung.

        Der eigene Schuber zaehlt dabei nicht mehr mit — er traegt noch `open`, ist aber
        schon am Ausfahren, daher `:not(.is-closing)`.
      */
      if (
        !document.querySelector(
          'dialog.drawer[open]:not(.is-closing), dialog.header__nav[open]:not(.is-closing)'
        )
      ) {
        document.documentElement.removeAttribute('data-schuber-dunkel');
      }
      this.dialog.classList.remove('is-closing', 'drawer--ueber');
      this.dialog.close();

      stapelPflegen();
      ankuendigungZurueck();
      /* Nicht uebernommene Eingaben verwerfen, damit beim naechsten Oeffnen der echte Stand steht */
      this.dialog.querySelectorAll('form[data-reset-on-close]').forEach((form) => form.reset());
      this.closing = false;
      /* Ohne `preventScroll` faehrt der Browser zum Ausloeser — und der liegt nach einem
         Variantenwechsel womoeglich gar nicht mehr dort, wo er beim Oeffnen war. */
      this.trigger?.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else setTimeout(finish, 250);
  }
}

customElements.define('kg-dialog', KgDialog);

/*
  Bildschirmtastatur. Safari auf dem iPhone verkleinert den Layoutbereich nicht, es
  schiebt ihn nach oben; der Versatz steht in visualViewport.offsetTop. Chrome auf
  Android laesst den Layoutbereich stehen und verkleinert nur den sichtbaren Teil.
  Beides deckt derselbe Weg ab, den auch die einschlaegigen Anleitungen empfehlen:
  nicht unten verankern, sondern die Unterkante des sichtbaren Bereichs ausrechnen
  und von dort nach oben aufbauen.

  Gemeldet werden:
  - --vv-bottom: Unterkante des sichtbaren Bereichs, in Layout-Koordinaten
  - --vv-height: Hoehe des sichtbaren Bereichs
  - --keyboard-inset: wie viel unten verdeckt ist; daran wird abgelesen, ob die Tastatur steht
  - --sheet-basis: sichtbare Hoehe ohne Tastatur, fuer Schuber, die stehen bleiben sollen
*/
const sicht = window.visualViewport;
if (sicht) {
  let ohneTastatur = 0;
  let laeuft = false;
  /* Zuletzt gueltiger Versatz und der Wert, der auf seine Bestaetigung wartet. */
  let versatz = 0;
  let versatzKandidat = 0;
  const gemerkt = {};

  /* Nur schreiben, wenn sich wirklich etwas aendert: Jeder Schreibvorgang kostet Layout */
  const setzen = (name, wert, schwelle) => {
    if (gemerkt[name] !== undefined && Math.abs(wert - gemerkt[name]) <= schwelle) return;
    gemerkt[name] = wert;
    document.documentElement.style.setProperty(name, `${wert}px`);
  };

  const rechnen = () => {
    laeuft = false;
    /*
      Liegt eine durchscheinende Systemleiste ueber der Seite, reicht der sichtbare Bereich
      weiter nach unten, als man benutzen kann. Ihre Hoehe wird von aussen gemeldet; hier
      faellt sie heraus, und damit stimmen alle Masse, die daran haengen, auf einen Schlag.
    */
    const appLeiste = window.__kgAppLeiste || 0;
    const sichtbar = Math.round(sicht.height + sicht.offsetTop) - appLeiste;
    const hoehe = Math.round(sicht.height) - appLeiste;
    setzen('--vv-height', hoehe, 2);
    setzen('--vv-bottom', sichtbar, 2);
    /*
      Ohne Schwelle, anders als die uebrigen: Diese Variable positioniert die Oberkante
      jedes Schubers. Eine gesparte Schreiboperation ist dort kein gesparter Aufwand,
      sondern ein sichtbarer Versatz — beim Hochziehen am Seitenanfang geht `offsetTop`
      kurz ins Minus, und mit Schwelle bleibt der letzte Wert haengen. Gemessen am Geraet:
      der Schuber sass danach 4 px zu hoch, mal 2, mal 1.
    */
    /*
      Ein Ausreisser im Versatz wird nicht sofort uebernommen, solange die Tastatur offen ist.

      Im Video vom 08.10. sprang beim Wechsel von einem Feld zum naechsten alles zusammen —
      Schuber, Seite und Kopfzeile — fuer ein Bild um 58 px nach unten und danach zurueck. Der
      Grund: Safari meldete fuer diesen Moment einen Versatz, den der sichtbare Bereich gar
      nicht hatte. Ausgeglichen wuerde also etwas, was nicht passiert ist, und genau dieser
      Ausgleich war das Zucken.

      Deshalb muss ein neuer Wert zweimal hintereinander gemessen werden, bevor er gilt. Ein
      einzelner Ausreisser faellt damit weg, ein echter Versatz kommt ein Bild spaeter an —
      sechzehn Millisekunden, die niemand sieht.

      Nur bei offener Tastatur: Ohne sie zaehlt jede Messung sofort. Dort geht es um das Ziehen
      am Seitenanfang, wo `offsetTop` kurz ins Minus geht und ein verzoegerter Wert den Schuber
      sichtbar nachhinken liesse.
    */
    const roh = Math.round(sicht.offsetTop);
    const tastaturOffen = ohneTastatur > 0 && hoehe < ohneTastatur - 150;
    document.documentElement.toggleAttribute('data-tastatur', tastaturOffen);
    if (!tastaturOffen || roh === versatzKandidat) versatz = roh;
    versatzKandidat = roh;
    setzen('--vv-top', versatz, 0);

    /*
      Geht in einem Schuber die Tastatur auf, schiebt Safari den sichtbaren Bereich im Layout
      nach unten — am Geraet gemessen 129 px. Der Schuber folgt dem ueber `--vv-top`; die
      festgehaltene Seite dahinter tut es nicht und wandert deshalb sichtbar nach oben. Das
      sieht aus, als sei etwas kaputt.

      Verhindern laesst sich Safaris Verschiebung nicht, ausgleichen schon: Die Seite geht um
      denselben Betrag mit. Bewusst ueber `top` und nicht ueber `translate` — eine
      Verschiebung macht den Koerper zum Bezugsrahmen fuer alles, was darin fest positioniert
      ist, und Toast oder Kaufleiste saessen dann an der falschen Stelle.

      Nur bei offener Tastatur. Sonst ist der Versatz ohnehin null — und wenn der Browser
      trotzdem einen meldet, etwa beim Gummiband am Seitenende, verschoebe der Ausgleich die
      Seite ohne Grund. Beim Oeffnen der Sortenauswahl sah man genau das als Flackern oben.
    */
    if (gesperrt) {
      document.body.style.top = `${(tastaturOffen ? versatz : 0) - merkePosition}px`;
    }

    /*
      Hoehe ohne Tastatur, fuer Schuber mit `drawer--sheet-keep`.

      Gezaehlt wird die zuletzt gesehene Hoehe ohne Tastatur. Groesser wird sie immer
      uebernommen; kleiner nur dann, wenn gerade niemand in einem Feld steht.

      Ein Maximum aus `innerHeight` und der Unterkante des sichtbaren Bereichs taugt dafuer
      nicht: Die Unterkante ist in Layout-Koordinaten gemessen, also keine Hoehe, und auf dem
      iPhone schiebt eine offene Tastatur den Layoutbereich — dort kommen 1522 px bei einem
      796 px hohen Bildschirm heraus.

      Eine Toleranz in Pixeln taugt ebenfalls nicht: Die Tastatur faehrt nicht in einem Sprung
      auf, sondern in Schritten, und jeder einzelne liegt unter jeder sinnvollen Schwelle. Die
      Basis wandert damit Stueck fuer Stueck nach unten, und nach dem Schliessen stuende der
      Schuber auf halber Hoehe.

      Der Fokus ist das verlaessliche Zeichen: Eine Tastatur geht nur auf, wenn ein Feld sie
      gerufen hat. Schrumpft der Bereich ohne Fokus, ist es die Adressleiste.
    */
    const tippt =
      document.activeElement &&
      document.activeElement.matches?.('input, textarea, select, [contenteditable]');
    if (!ohneTastatur || hoehe >= ohneTastatur || !tippt) ohneTastatur = hoehe;
    setzen('--sheet-basis', ohneTastatur, 2);
    /*
      Flacher Bildschirm: Darunter bekommen auch die gewoehnlichen Schuber die Luecke der
      hohen. Auf einem kleinen Geraet oder im Querformat ist der Unterschied zwischen beiden
      Stufen Luxus — dort zaehlt nur, dass genug Flaeche uebrig bleibt.

      Gemessen, nicht per `@media (max-height)`: Die Medienabfrage sieht den Layoutbereich,
      der auf dem iPhone groesser ist als das, was man wirklich sieht. Gemeint ist aber die
      verfuegbare Hoehe, und die steht hier ohnehin schon.
    */
    /*
      Zwei Schwellen statt einer: Ein Geraet mit knapp 650 px wechselt sonst bei jedem Auf-
      und Zuklappen der Adressleiste die Stufe, und der Schuber springt um ueber 50 px.
      Hinein geht es unter 650, heraus erst ueber 690.
    */
    const wurzel = document.documentElement;
    const kurz = wurzel.hasAttribute('data-kurz');
    wurzel.toggleAttribute('data-kurz', kurz ? ohneTastatur < 690 : ohneTastatur < 650);
    /*
      Zweite, engere Schwelle. Darunter faellt im Warenkorb auch die Reihe der Zahlungsarten
      weg: Bei diesem Platz haben die Positionen und die Summe Vorrang.
    */
    const sehrKurz = wurzel.hasAttribute('data-sehr-kurz');
    wurzel.toggleAttribute('data-sehr-kurz', sehrKurz ? ohneTastatur < 540 : ohneTastatur < 500);
    setzen('--keyboard-inset', Math.max(0, ohneTastatur - hoehe), 2);
  };

  /*
  Beim Tippen in einem Schuber: Das Feld, in dem der Besucher steht, muss sichtbar bleiben.
  Der Browser scrollt zwar selbst dorthin, aber er tut es, bevor die Tastatur fertig
  aufgefahren ist — danach steht das Feld wieder darunter. Deshalb hier noch einmal, kurz
  nachdem sich der sichtbare Bereich zuletzt geaendert hat.

  `block: 'nearest'` statt `center`: Ein Feld, das schon zu sehen ist, soll sich nicht bewegen.
*/
const istFeld = (el) => Boolean(el?.matches?.('input, textarea, select'));

/*
  Schuber, die sich nach dem Neuladen von selbst oeffnen — bisher nur der Kontaktschuber nach
  dem Absenden. Ohne das waere die Bestaetigung hinter einem geschlossenen Schuber verborgen,
  und der Besucher wuesste nicht, ob seine Nachricht angekommen ist.
*/
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-contact-done]').forEach((bestaetigung) => {
    bestaetigung.closest('kg-dialog')?.open?.();
  });
});

/* „Weitere Nachricht schreiben": Bestaetigung weg, Formular zurueck — ohne Neuladen. */
document.addEventListener('click', (ereignis) => {
  const knopf = ereignis.target.closest?.('[data-contact-again]');
  if (!knopf) return;
  const bereich = knopf.closest('.contact-form__body');
  const felder = bereich?.querySelector('[data-contact-fields]');
  if (!felder) return;
  bereich.querySelector('[data-contact-done]')?.setAttribute('hidden', '');
  felder.removeAttribute('hidden');
  /* Das erste echte Feld, nicht die verborgenen, die Shopify dem Formular mitgibt. */
  felder.querySelector('input:not([type="hidden"]), textarea')?.focus();
});

/*
  Das Feld selbst ins Bild holen — aber nur innerhalb des Schubers. `scrollIntoView` wuerde
  jeden scrollbaren Vorfahren mitnehmen, auch die Seite dahinter; die ruckelt dann sichtbar
  unter dem Schuber, obwohl sie gesperrt sein soll.
*/
function insBild(feld, schuber) {
  const flaeche = feld.closest('.drawer__body, .drawer__scroller') || schuber;
  if (!flaeche || flaeche.scrollHeight <= flaeche.clientHeight) return;
  const f = feld.getBoundingClientRect();
  const b = flaeche.getBoundingClientRect();
  const rand = 12;
  if (f.top < b.top + rand) flaeche.scrollTop += f.top - b.top - rand;
  else if (f.bottom > b.bottom - rand) flaeche.scrollTop += f.bottom - b.bottom + rand;
}

/*
  Das Feld in die obere Haelfte des Schubers holen, bevor die Tastatur aufgeht.

  Hintergrund: Steht das Feld dort, wo gleich die Tastatur liegt, hebt Safari den ganzen
  Layoutbereich an, um es freizulegen — am Geraet gemessen 129 px. Dabei wandert auch die Seite
  hinter dem Schuber nach oben, und das sieht aus, als sei etwas kaputt. Verhindern laesst sich
  das nicht, wohl aber der Anlass: Ist das Feld schon oben, hat Safari nichts anzuheben.

  Nur wenn die Tastatur noch zu ist. Wechselt der Besucher zwischen zwei Feldern, ist sie
  offen, und dann zaehlt nur noch, dass das neue Feld sichtbar wird — ohne weiteren Sprung.
*/
function nachOben(feld, schuber) {
  const flaeche = feld.closest('.drawer__body, .drawer__scroller') || schuber;
  if (!flaeche || flaeche.scrollHeight <= flaeche.clientHeight) return;
  const f = feld.getBoundingClientRect();
  const b = flaeche.getBoundingClientRect();
  const ziel = b.top + Math.min(72, b.height * 0.12);
  if (f.top <= ziel + 1) return;
  flaeche.scrollTop += f.top - ziel;
}

document.addEventListener('focusin', (ereignis) => {
  const feld = ereignis.target;
  if (!istFeld(feld)) return;
  const schuber = feld.closest('dialog[open]');
  if (!schuber) return;
  /* Solange getippt wird, loest sich der klebende Knopf — siehe section-help-center.css. */
  schuber.setAttribute('data-tippt', '');

  const tastaturZu =
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--keyboard-inset')) < 40;
  if (tastaturZu) nachOben(feld, schuber);
  else insBild(feld, schuber);

  /* Noch einmal, wenn die Tastatur fertig aufgefahren ist — vorher stimmt die Hoehe nicht. */
  window.setTimeout(() => {
    if (document.activeElement === feld) insBild(feld, schuber);
  }, 350);
});

document.addEventListener('focusout', (ereignis) => {
  const schuber = ereignis.target.closest?.('dialog[open]');
  if (!schuber) return;
  /*
    Erst im naechsten Durchlauf pruefen, wohin der Fokus gewandert ist: Zwischen zwei Feldern
    liegt ein kurzer Moment ohne Fokus, und ohne die Pause spraenge der Knopf dabei kurz an
    seinen Platz zurueck.
  */
  window.setTimeout(() => {
    if (!istFeld(document.activeElement) || !schuber.contains(document.activeElement)) {
      schuber.removeAttribute('data-tippt');
    }
  }, 50);
});

/* Nur einmal je Bild rechnen, sonst rechnet jedes Scrollereignis mit */
  const merken = () => {
    if (laeuft) return;
    laeuft = true;
    window.requestAnimationFrame(rechnen);
  };
  sicht.addEventListener('resize', merken);
  sicht.addEventListener('scroll', merken);
  window.addEventListener('resize', merken);

  /*
    Sicherheitsnetz um jeden Fokuswechsel. Das Auf- und Zufahren der Tastatur dauert auf dem
    Geraet mehrere hundert Millisekunden, und nicht jeder Browser meldet dabei zuverlaessig
    den letzten Stand — fehlt die letzte Meldung, bleibt der Schuber auf der Hoehe stehen, die
    er neben der Tastatur hatte. Nachmessen kostet nichts: `rechnen()` schreibt nur, wenn sich
    wirklich etwas geaendert hat.
  */
  for (const art of ['focusin', 'focusout']) {
    document.addEventListener(art, () => {
      for (const verzoegerung of [0, 150, 400, 800]) window.setTimeout(merken, verzoegerung);
    });
  }
  window.addEventListener('orientationchange', () => {
    /* Beim Drehen aendert sich die Hoehe um weit mehr als eine Tastatur. Ohne Ruecksetzen
       hielte die Regel oben den Wert der alten Lage fest. */
    ohneTastatur = 0;
    window.setTimeout(merken, 300);
  });
  merken();
}

/*
  Ankuendigungszeile aus dem Weg, solange ein Bottom Sheet offen ist.

  Ueber dem Schuber bleibt bewusst ein Streifen Seite sichtbar — er sagt, dass man dort
  tippen oder ziehen kann, um zu schliessen. Steht die Seite aber ganz oben, fuellt die
  Ankuendigungszeile genau diesen Streifen, und statt Logo und Kopfzeile sieht man einen
  Farbbalken. Dann weicht sie, zusammen mit dem Einfahren des Schubers.

  Nur ganz oben: Weiter unten ist die Zeile ohnehin weggescrollt, und ein Einklappen wuerde
  die Seite hinter dem Schuber verschieben, ohne dass es etwas bringt.
*/
const SHEET_SELEKTOR = 'dialog.drawer--sheet[open], dialog.drawer--sheet-mobile[open]';

function istSheet(dialog) {
  if (!dialog) return false;
  if (dialog.classList.contains('drawer--sheet')) return true;
  return dialog.classList.contains('drawer--sheet-mobile') && window.matchMedia('(max-width: 1023.98px)').matches;
}

function ankuendigung() {
  const leiste = document.querySelector('.announcement-bar');
  return leiste?.closest('.shopify-section') || null;
}

function ankuendigungWeg() {
  const bereich = ankuendigung();
  if (!bereich) return;
  const hoehe = bereich.offsetHeight;
  /* Nur wenn die Zeile im sichtbaren Streifen steht, also die Seite noch nicht daran vorbei ist. */
  if (!hoehe || seitenScroll() > hoehe) return;
  document.documentElement.style.setProperty('--announcement-height', `${hoehe}px`);
  document.documentElement.setAttribute('data-sheet-top', '');
}

function ankuendigungZurueck() {
  /* Erst, wenn wirklich nichts mehr offen ist — im Drawer liegen Schuber ineinander. */
  if (offeneSheets > 0 || [...document.querySelectorAll(SHEET_SELEKTOR)].some(istSheet)) return;
  document.documentElement.removeAttribute('data-sheet-top');
}

/*
  Zwei Ereignisse fuer alles, was sich wie ein Schuber verhaelt, ohne ein `<dialog>` zu sein.

  Die mobile Navigation ist so ein Fall: Sie teilt sich ihr Markup mit der Leiste am Rechner
  und kann deshalb nicht in einen `<dialog>` wandern — der waere am Rechner unsichtbar. Statt
  die Mechanik ein zweites Mal zu bauen, meldet sie sich hier an.

  Bewusst ueber Ereignisse und nicht ueber einen Import: Relative Modul-Importe tragen im
  Theme keine Version in der Adresse, Browser behalten dann die alte Datei im Zwischenspeicher.
*/
let offeneSheets = 0;
document.addEventListener('sheet:open', () => {
  offeneSheets += 1;
  ankuendigungWeg();
});
document.addEventListener('sheet:close', () => {
  offeneSheets = Math.max(0, offeneSheets - 1);
  ankuendigungZurueck();
});

/*
  Seite hinter einem offenen Drawer wirklich festhalten. `overflow: hidden` am html
  reicht auf dem iPhone nicht, dort scrollt der Finger die Seite darunter weiter,
  sobald die Liste im Drawer am Ende ist oder gar nicht scrollt. Der Koerper wird deshalb festgelegt
  und die Position gemerkt, damit nach dem Schliessen nichts springt.
  Der Beobachter greift fuer jeden Dialog mit scroll-lock, egal wer ihn oeffnet.
*/
let merkePosition = 0;
let gesperrt = false;

/*
  Der wahre Stand der Seite. Liegt der Koerper fest, meldet `scrollY` null — wer dann danach
  fragt, haelt jede Seite fuer ganz oben. Im Drawer liegen Schuber ineinander, und der innere
  faende so immer null vor.
*/
function seitenScroll() {
  return gesperrt ? merkePosition : window.scrollY;
}

function sperrePruefen() {
  /*
    Auch die mobile Navigation zaehlt. Sie ist kein Dialog, und `overflow: hidden` am html
    allein reicht auf dem iPhone nicht — dort scrollt der Finger die Seite darunter weiter.
    Als Schuber faellt das staerker auf, weil Ziehen nach unten dort die naheliegende Geste ist.
  */
  const soll =
    Boolean(
      document.querySelector(
        'dialog[scroll-lock][open]:not(.is-closing), details[scroll-lock][open]'
      )
    ) ||
    document.documentElement.classList.contains('is-scroll-locked');
  if (soll === gesperrt) return;
  gesperrt = soll;
  const koerper = document.body;
  document.documentElement.toggleAttribute('data-gesperrt', soll);
  if (soll) {
    merkePosition = window.scrollY;
    koerper.style.position = 'fixed';
    koerper.style.top = `-${merkePosition}px`;
    koerper.style.left = '0';
    koerper.style.width = '100%';
  } else {
    koerper.style.position = '';
    koerper.style.top = '';
    koerper.style.left = '';
    koerper.style.width = '';
    /*
      Ausdruecklich ohne Animation: Am html steht scroll-behavior: smooth, und weil der
      Koerper waehrend der Sperre festliegt, steht die Seite beim Loesen kurz auf null.
      Ein normales scrollTo wuerde von dort sichtbar an die alte Stelle fahren.
    */
    window.scrollTo({ top: merkePosition, left: 0, behavior: 'instant' });
  }
}

new MutationObserver(sperrePruefen).observe(document.documentElement, {
  subtree: true,
  attributes: true,
  attributeFilter: ['open'],
});

/*
  Eigener Beobachter nur fuer das Wurzelelement: `class` im ganzen Baum zu beobachten waere
  ein Dauerfeuer, hier aendert sich genau eine Klasse.
*/
new MutationObserver(sperrePruefen).observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['class'],
});

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-dialog-open]');
  if (!trigger) return;
  const target = document.getElementById(trigger.dataset.dialogOpen);
  if (!(target instanceof KgDialog)) return;
  event.preventDefault();
  target.open(trigger);
});

/*
  Farbe der Browserleisten, solange ein Schuber offen ist.

  Die Leisten nehmen die Farbe aus `theme-color`, solange eine Angabe da ist. Fehlt sie,
  bleiben sie durchsichtig — genau das soll im Normalfall gelten, deshalb gilt die
  Angabe nur fuer die Dauer eines offenen Schubers und nehmen sie danach wieder weg.

  Die Farbe ist dieselbe, die hinter dem Schuber entsteht: Textfarbe zur Haelfte ueber dem
  Grund, also das Ergebnis der Abdunklung. Sie kommt aus den Farbschema-Variablen und folgt
  damit dem eingestellten Schema.

  Auf Android ist das der einzige Weg zu einer mitgefaerbten Leiste.
*/
function schuberFarbe() {
  const stil = getComputedStyle(document.documentElement);
  const lies = (name) => stil.getPropertyValue(name).trim().split(/[\s,]+/).map(Number);
  const text = lies('--color-text');
  const grund = lies('--color-background');
  if (text.length < 3 || grund.length < 3 || [...text, ...grund].some(Number.isNaN)) return null;
  const misch = (i) => Math.round(text[i] * 0.5 + grund[i] * 0.5);
  return `rgb(${misch(0)} ${misch(1)} ${misch(2)})`;
}

/* Merkt sich den Stand vor dem Eingriff: null heisst, es gab das Tag vorher nicht. */
let farbStand = null;

function farbeAn() {
  if (farbStand) return;
  const farbe = schuberFarbe();
  if (!farbe) return;

  const vorhandene = [...document.querySelectorAll('meta[name="theme-color"]')];
  if (vorhandene.length) {
    farbStand = vorhandene.map((tag) => ({ tag, vorher: tag.getAttribute('content') }));
    vorhandene.forEach((tag) => tag.setAttribute('content', farbe));
    return;
  }

  const tag = document.createElement('meta');
  tag.setAttribute('name', 'theme-color');
  tag.setAttribute('content', farbe);
  document.head.append(tag);
  farbStand = [{ tag, vorher: null }];
}

function farbeAus() {
  if (!farbStand) return;
  farbStand.forEach(({ tag, vorher }) => {
    if (vorher === null) tag.remove();
    else tag.setAttribute('content', vorher);
  });
  farbStand = null;
}

/* Gezaehlt wird, weil Schuber ineinander liegen koennen — der Warenkorb oeffnet den Bearbeiten-Schuber. */
let offeneFarbe = 0;

function farbeOeffnet() {
  offeneFarbe += 1;
  farbeAn();
}

function farbeSchliesst() {
  offeneFarbe = Math.max(0, offeneFarbe - 1);
  if (!offeneFarbe) farbeAus();
}

document.addEventListener('dialog:open', farbeOeffnet);
document.addEventListener('sheet:open', farbeOeffnet);
document.addEventListener('dialog:close', farbeSchliesst);
document.addEventListener('sheet:close', farbeSchliesst);

/*
  Stapelverwaltung der Schuber.

  Jeder Schuber bekommt beim Oeffnen einen Rang; daraus ergibt sich, wer ueber wem liegt.
  Der oberste braucht nichts, alle darunter werden abgedunkelt (`drawer--unten`) und alle
  ausser dem untersten gelten als aufgesetzt (`drawer--ueber`).

  Aufgeraeumt wird je Rang, nicht pauschal, sobald nichts mehr aufgesetzt ist: Bei drei
  Ebenen ist nach dem Schliessen der dritten die zweite frei, die erste aber nicht — die
  Abdunklung bliebe sonst stehen.

  Die Navigation zaehlt mit, sie ist in der Schuber-Darstellung ein Schuber wie jeder andere.
*/
const SCHUBER = 'dialog.drawer[open], dialog.header__nav[open]';
/* Der Zaehler steht am Wurzelelement, damit auch header.js denselben benutzen kann. */
function naechsterRang() {
  const wurzel = document.documentElement;
  const naechster = Number(wurzel.dataset.schuberRang || 0) + 1;
  wurzel.dataset.schuberRang = String(naechster);
  return naechster;
}

function stapelPflegen() {
  const offen = [...document.querySelectorAll(SCHUBER)].sort(
    (a, b) => Number(a.dataset.schuberRang || 0) - Number(b.dataset.schuberRang || 0)
  );
  document.querySelectorAll('dialog.drawer--unten, dialog.drawer--ueber').forEach((schuber) => {
    if (!offen.includes(schuber)) schuber.classList.remove('drawer--unten', 'drawer--ueber');
  });
  offen.forEach((schuber, stelle) => {
    schuber.classList.toggle('drawer--unten', stelle < offen.length - 1);
    schuber.classList.toggle('drawer--ueber', stelle > 0);
  });
}

/* Die Navigation meldet sich hier an, statt die Mechanik ein zweites Mal zu bauen. */
document.addEventListener('schuber:stapel', stapelPflegen);
